import React, { ReactElement, useMemo, useRef, useState } from 'react';
import { styled } from '../../styles';
import { BpDesignSystem } from '../../types';
import { filterNotEmpty } from '../../utils';
import { Keys } from '../../utils/keysUtil';
import { DragAndDropProps } from '../DragAndDrop';
import { FormGroup } from '../Form/FormGroup';
import { FormHelperText } from '../Form/index';
import { FormLabel } from '../Form/FormLabel';
import { IconButton } from '../IconButton/IconButton';
import { Typography } from '../Typography/Typography';
import { TransferListOption } from './TransferList.types';
import { getOptions } from './TransferList.utils';
import { TransferListCheckboxList, TransferListCheckboxListProps } from './TransferListCheckboxList';

type CheckboxListProps = Required<TransferListCheckboxListProps<any>>;

export type TransferListProps<VALUE extends string> = BpDesignSystem.Props<{
  /* min height 250 */
  height?: number | '100%';
  options: TransferListOption<VALUE>[];
  optionsWithOriginalNames?: TransferListOption<VALUE>[];
  values: VALUE[];
  /* use this value to filter values */
  searchValue?: string;
  onChange: (values: VALUE[], options: TransferListOption<VALUE>[]) => void;
  label?: string;
  subLabel?: string;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  draggable?: boolean;
  onLabelChange?: (option: TransferListOption<VALUE>) => void;
  slotProps?: {
    dragAndDrop?: Partial<CheckboxListProps['slotProps']['dragAndDrop']>;
    checkboxList?: Pick<TransferListCheckboxListProps<VALUE>, 'itemSize'>;
  };
}>;

export const TransferList = <VALUE extends string>({
  options = [],
  optionsWithOriginalNames,
  height = '100%',
  values = [],
  searchValue = '',
  dataTest = 'transfer-list',
  onChange,
  label,
  subLabel,
  helperText,
  errorText,
  error,
  disabled,
  loading,
  required,
  style,
  sx,
  className,
  draggable,
  slotProps,
  onLabelChange,
  ...props
}: TransferListProps<VALUE>): ReactElement | null => {
  const { leftOptions, rightOptions } = useMemo(
    () => getOptions(options, values, searchValue),
    [values, searchValue, options]
  );
  const [checkedLeft, setCheckedLeft] = useState<TransferListOption<VALUE>[]>([]);
  const [checkedRight, setCheckedRight] = useState<TransferListOption<VALUE>[]>([]);
  const disabledAll = !!(disabled || loading);
  const moveToRightButtonRef = useRef<HTMLButtonElement>(null);
  const moveToLeftButtonRef = useRef<HTMLButtonElement>(null);

  const handleToggleRight = (option: TransferListOption<VALUE>) => {
    setCheckedRight((prevChecked) => {
      if (prevChecked.find((prev) => prev.value === option.value)) {
        return prevChecked.filter((it) => it.value !== option.value);
      }
      return [...prevChecked, option];
    });
  };

  const handleToggleLeft = (option: TransferListOption<VALUE>) => {
    setCheckedLeft((prevChecked) => {
      if (prevChecked.find((prev) => prev.value === option.value)) {
        return prevChecked.filter((it) => it.value !== option.value);
      }
      return [...prevChecked, option];
    });
  };

  const checkAllLeft = () => {
    setCheckedLeft(leftOptions.length !== checkedLeft.length ? leftOptions : []);
  };

  const checkAllRight = () => {
    setCheckedRight(rightOptions.length !== checkedRight.length ? rightOptions : []);
  };

  const applyChanges = (options: TransferListOption<VALUE>[]) => {
    onChange(
      options.map((it) => it.value),
      options
    );
  };

  const handleMoveCheckedToRight = () => {
    const right = filterNotEmpty(values.map((it) => options.find((option) => option.value === it)));
    applyChanges([...right, ...checkedLeft]);
    setCheckedRight([]);
    setCheckedLeft([]);
  };

  const handleMoveCheckedToLeft = () => {
    const right = filterNotEmpty(values.map((it) => options.find((option) => option.value === it)));
    const checkedRightValues = checkedRight.map((it) => it.value);
    applyChanges(right.filter((option) => !checkedRightValues.includes(option.value)));
    setCheckedLeft([]);
    setCheckedRight([]);
  };

  const handleDragEnd: DragAndDropProps['onDragEnd'] = (result) => {
    if (!result.destination) {
      return;
    }
    if (result.source.index === result.destination.index) {
      return;
    }
    applyChanges(reorder(rightOptions, result.source.index, result.destination.index));
  };

  return (
    <StyledFormGroup style={style} sx={sx} className={className} $height={height} dataTest={dataTest}>
      <FormLabel required={required} dataTest={`${dataTest}-label`} shrink>
        {label}
      </FormLabel>
      {!!subLabel && (
        <StyledDescription variant="body" small>
          {subLabel}
        </StyledDescription>
      )}

      <StyledGrid $height={height} {...props}>
        <div>
          <TransferListCheckboxList<VALUE>
            direction="left"
            options={leftOptions}
            disabledAll={disabledAll}
            disabled={leftOptions.length === 0 || !!searchValue || disabledAll}
            checked={checkedLeft}
            error={error}
            onCheck={handleToggleLeft}
            checkAll={checkAllLeft}
            searchValue={searchValue}
            dataTest={`${dataTest}-left-list`}
            {...slotProps?.checkboxList}
            onKeyDown={(e) => {
              if (Keys.oneOf(e, 'ArrowRight')) {
                moveToRightButtonRef.current?.focus();
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          />
        </div>
        <div className="controls-wrapper">
          <div>
            <IconButton
              onClick={handleMoveCheckedToRight}
              color={checkedLeft.length !== 0 ? 'active' : undefined}
              disabled={checkedLeft.length === 0}
              dataTest={`${dataTest}-move-to-right`}
              iconName="keyboard_arrow_right"
              ref={moveToRightButtonRef}
            />
            <IconButton
              onClick={handleMoveCheckedToLeft}
              color={checkedRight.length !== 0 ? 'active' : undefined}
              disabled={checkedRight.length === 0}
              dataTest={`${dataTest}-move-to-left`}
              iconName="keyboard_arrow_left"
              ref={moveToLeftButtonRef}
            />
          </div>
        </div>
        <div>
          <TransferListCheckboxList<VALUE>
            direction="right"
            options={rightOptions}
            optionsWithOriginalNames={optionsWithOriginalNames}
            checked={checkedRight}
            error={error}
            disabledAll={disabledAll}
            disabled={rightOptions.length === 0 || !!searchValue || disabledAll}
            onCheck={handleToggleRight}
            checkAll={checkAllRight}
            searchValue={searchValue}
            onDragEnd={draggable ? handleDragEnd : undefined}
            slotProps={slotProps}
            dataTest={`${dataTest}-right-list`}
            onLabelChange={onLabelChange}
            {...slotProps?.checkboxList}
            onKeyDown={(e) => {
              if (Keys.oneOf(e, 'ArrowLeft')) {
                moveToLeftButtonRef.current?.focus();
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          />
        </div>
      </StyledGrid>
      <FormHelperText helperText={helperText} errorText={errorText} error={error} />
    </StyledFormGroup>
  );
};

const StyledGrid = styled('div')<{ $height: TransferListProps<any>['height'] }>`
  display: grid;
  grid-template-columns: 1fr 40px 1fr;
  gap: ${({ theme }) => theme.spacingSize.m};
  grid-template-rows: ${({ $height }) => ($height === '100%' ? 'auto' : `${$height}px`)};
  flex-grow: ${({ $height }) => ($height === '100%' ? '2' : undefined)};
  position: relative;

  & > * {
    height: 100%;
  }

  .controls-wrapper div {
    position: sticky;
    top: ${({ $height }) => ($height === '100%' ? 85 : ($height ?? 250) / 2 - 40)}px;
    margin-top: ${({ $height }) => ($height === '100%' ? 85 : ($height ?? 250) / 2 - 40)}px;
  }
`;

const StyledFormGroup = styled(FormGroup)<{ $height: TransferListProps<any>['height'] }>`
  height: ${({ $height }) => ($height === '100%' ? '100%' : undefined)};
  
  & > label + .MuiGrid2-root,
  & > .MuiTypography-root + .MuiGrid2-root {
    margin-top: ${({ theme }) => theme.spacingSize.m};
  }
`;

const StyledDescription = styled(Typography)`
    && {
        margin-bottom: 2px;
    }
`;

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  if (removed) {
    result.splice(endIndex, 0, removed);
  }

  return result;
}
